import template from './uc-free-product-manager.html.twig';
import './uc-free-product-manager.scss';

const { Mixin } = Shopware;
const { Criteria } = Shopware.Data;

/**
 * uc-free-product-manager
 *
 * CRUD listing + form modal for the "Free products" rules
 * (sellops_uc_free_product). Modelled on uc-milestone-manager.
 */
Shopware.Component.register('uc-free-product-manager', {
    template,

    // 'acl' is injected as a service (per Shopware's own ACL docs), NOT a
    // mixin — Mixin.getByName('acl') doesn't exist and throws
    // "The mixin 'acl' is not registered." Injecting it here exposes it as
    // `this.acl` / `acl` in the template automatically, same as any other
    // injected service.
    inject: ['repositoryFactory', 'acl'],

    mixins: [
        Mixin.getByName('notification'),
    ],

    data() {
        return {
            rules: null,
            isLoading: false,
            // Distinguishes "no rules created yet" from "the list failed to
            // load" — see the same field on uc-milestone-manager for why.
            loadError: false,
            showModal: false,
            showFormBody: false,
            isNewRule: false,
            isSaving: false,
            deleteId: null,
            showDeleteConfirm: false,
            isDeleting: false,

            form: this._emptyForm(),
        };
    },

    computed: {
        repository() {
            return this.repositoryFactory.create('sellops_uc_free_product');
        },

        productRepository() {
            return this.repositoryFactory.create('product');
        },

        // sw-entity-multi-id-select requires a Repository instance (not an
        // entity name like sw-entity-single-select)
        salesChannelRepository() {
            return this.repositoryFactory.create('sales_channel');
        },

        /**
         * One entry per configurable product — variants picked in storefront.
         * Excludes inactive products: FreeProductGiftResolver::resolve() looks
         * the gift up via the sales-channel product repository, which only
         * ever returns active + sales-channel-assigned products — picking an
         * inactive one here would silently create a rule that can never
         * actually trigger. Matches the same active-only filter already used
         * by the Cross-Sell rule builder's product picker.
         */
        productCriteria() {
            const criteria = new Criteria(1, 25);
            criteria.addFilter(Criteria.equals('parentId', null));
            criteria.addFilter(Criteria.equals('active', true));
            return criteria;
        },

        sortedRules() {
            if (!this.rules) return [];
            return [...this.rules].sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0));
        },

        // Built-in default icon for free-product gift rules — mirrors the
        // same free-gift.svg the storefront falls back to for the
        // 'free_product' reward type (see ProgressBarResolver /
        // progress-bar.html.twig), so the popup shows the same image
        // shoppers would actually see before the merchant uploads their own.
        freeProductDefaultIconUrl() {
            // See the matching note in uc-milestone-manager's
            // milestoneDefaultIconUrl() — assetsPath already ends in
            // '/bundles/', so no 'bundles/' prefix is added here.
            const assetFilter = Shopware.Filter.getByName('asset');
            return assetFilter('sellopsultimatecart/img/milestones/free-gift.png');
        },

        // Celebration animation played once, client-side, the moment a
        // shopper unlocks this gift — mirrors uc-milestone-manager's own
        // animationTypeOptions. See docs/celebration-animations-architecture.md.
        animationTypeOptions() {
            return [
                { value: 'none',      label: this.$tc('sellops-ultimate-cart.freeProducts.animationType.none') },
                { value: 'confetti',  label: this.$tc('sellops-ultimate-cart.freeProducts.animationType.confetti') },
                { value: 'fireworks', label: this.$tc('sellops-ultimate-cart.freeProducts.animationType.fireworks') },
                { value: 'gift',      label: this.$tc('sellops-ultimate-cart.freeProducts.animationType.gift') },
                { value: 'premium',   label: this.$tc('sellops-ultimate-cart.freeProducts.animationType.premium') },
            ];
        },
    },

    created() {
        this.loadRules();
    },

    methods: {
        _emptyForm() {
            return {
                id:              null,
                active:          true,
                oncePerCustomer: false,
                activeFrom:      null,
                activeUntil:     null,
                salesChannelIds: [],
                productId:           null,
                iconUrl:             null,
                labelBefore:         '',
                labelAfter:          '',
                animationType:       'none',
                cartValue:           50,
                priority:            0,
            };
        },

        async loadRules() {
            this.isLoading = true;
            this.loadError = false;
            try {
                const criteria = new Criteria();
                criteria.addSorting(Criteria.sort('priority', 'DESC'));
                this.rules = await this.repository.search(criteria, Shopware.Context.api);
                await this._loadProductNames();
            } catch (e) {
                console.error('[UltimateCart] Failed to load free product rules:', e);
                this.rules = null;
                this.loadError = true;
            } finally {
                this.isLoading = false;
            }
        },

        /** Resolve product names for the listing */
        async _loadProductNames() {
            if (!this.rules || this.rules.length === 0) return;
            const ids = [...new Set(this.rules.map(r => r.productId).filter(Boolean))];
            if (!ids.length) return;
            try {
                const products = await this.productRepository.search(
                    new Criteria(1, ids.length).setIds(ids),
                    Shopware.Context.api
                );
                const names = {};
                products.forEach(p => { names[p.id] = p.translated?.name ?? p.name; });
                this.rules.forEach(r => { r.productName = names[r.productId] ?? '—'; });
            } catch {
                // listing falls back to showing the id
            }
        },

        openCreate() {
            this.form = this._emptyForm();
            this.isNewRule = true;
            this.showFormBody = true;
            this.showModal = true;
        },

        openEdit(rule) {
            this.form = {
                id:              rule.id,
                active:          rule.active,
                oncePerCustomer: rule.oncePerCustomer,
                activeFrom:      rule.activeFrom,
                activeUntil:     rule.activeUntil,
                salesChannelIds: rule.salesChannelIds ?? [],
                productId:           rule.productId,
                iconUrl:             rule.iconUrl ?? null,
                labelBefore:         rule.labelBefore ?? '',
                labelAfter:          rule.labelAfter ?? '',
                animationType:       rule.animationType ?? 'none',
                cartValue:           rule.cartValue,
                priority:            rule.priority,
            };

            this.isNewRule = false;
            this.showFormBody = true;
            this.showModal = true;
        },

        /**
         * Tear down selects first (their result lists teleport to <body>),
         * then unmount the modal. Doing both at once causes Vue insertBefore
         * errors; v-show leaves orphaned dropdowns.
         */
        async _closeFormModal() {
            if (document.activeElement instanceof HTMLElement) {
                document.activeElement.blur();
            }

            this.showFormBody = false;
            await this.$nextTick();
            await this.$nextTick();

            this.showModal = false;
            await this.$nextTick();
        },

        async saveRule() {
            if (!this.form.productId) {
                this.createNotificationError({
                    message: this.$tc('sellops-ultimate-cart.freeProducts.productRequired'),
                });
                return;
            }

            this.isSaving = true;
            try {
                let entity;
                if (this.isNewRule) {
                    entity = this.repository.create(Shopware.Context.api);
                } else {
                    entity = await this.repository.get(this.form.id, Shopware.Context.api);
                }

                // id is excluded from the copy on purpose (entity already
                // owns its own id) — delete instead of destructuring it into
                // an unused binding, so no eslint-disable comment is needed
                // at all (different lint configs across shopware-cli's build
                // pipeline and its `extension validate` command disagree on
                // whether the `@typescript-eslint/no-unused-vars` rule name
                // is even registered, so no single disable comment satisfies
                // both).
                const formData = { ...this.form };
                delete formData.id;
                Object.assign(entity, formData);
                await this.repository.save(entity, Shopware.Context.api);

                await this._closeFormModal();
                await this.loadRules();

                // Defer growl — also teleports to <body>; same flush as modal
                // teardown can throw insertBefore
                window.setTimeout(() => {
                    this.createNotificationSuccess({
                        message: this.$tc('sellops-ultimate-cart.freeProducts.saveSuccess'),
                    });
                }, 50);
            } catch (e) {
                console.error('[UltimateCart] Failed to save free product rule:', e);
                this.createNotificationError({
                    message: this.$tc('sellops-ultimate-cart.freeProducts.saveError'),
                });
            } finally {
                this.isSaving = false;
            }
        },

        async cancelModal() {
            await this._closeFormModal();
        },

        confirmDelete(id) {
            this.deleteId = id;
            this.showDeleteConfirm = true;
        },

        cancelDelete() {
            this.deleteId = null;
            this.showDeleteConfirm = false;
        },

        async executeDelete() {
            if (!this.deleteId) return;
            this.isDeleting = true;
            const idToDelete = this.deleteId;
            try {
                await this.repository.delete(idToDelete, Shopware.Context.api);

                // Close the confirm modal before the growl fires (teleport clash)
                this.isDeleting = false;
                this.deleteId = null;
                this.showDeleteConfirm = false;
                await this.$nextTick();
                await this.loadRules();

                this.createNotificationSuccess({
                    message: this.$tc('sellops-ultimate-cart.freeProducts.deleteSuccess'),
                });
            } catch (e) {
                console.error('[UltimateCart] Failed to delete free product rule:', e);
                this.isDeleting = false;
                this.deleteId = null;
                this.showDeleteConfirm = false;
                this.createNotificationError({
                    message: this.$tc('sellops-ultimate-cart.freeProducts.deleteError'),
                });
            }
        },

        validityLabel(rule) {
            const from  = rule.activeFrom  ? new Date(rule.activeFrom).toLocaleDateString()  : '…';
            const until = rule.activeUntil ? new Date(rule.activeUntil).toLocaleDateString() : '…';
            if (!rule.activeFrom && !rule.activeUntil) {
                return this.$tc('sellops-ultimate-cart.freeProducts.alwaysValid');
            }
            return `${from} – ${until}`;
        },
    },
});
